import {
  useQuery,
  useMutation,
  type UseQueryOptions,
  type UseMutationOptions
} from "react-query";
import { UserStorage, type UserData } from "@certa/common/utils/userStorage";
import {
  loginWithEmailPassword,
  loginWithOTP,
  submitMFAToken,
  checkAuthV2,
  logoutV2
} from "../services/authV2.services";
import { permissionService } from "../../main/src/modules/common/permissions/services";
import { useState } from "react";
import { useFlexibleMatch } from "../../routing/src/hooks/useFlexibleMatch";
import { queryClient } from "../utils/utils";
import {
  USER_QUERY_KEY,
  USER_PERMISSIONS_QUERY_KEY
} from "../constants/auth.constants";
import type { PermissionsAPIResponse } from "../types/permissions.types";
import type { LoginCredentials, OTPCredentials } from "@certa/types";
import { INSTANCE_CONFIG_QUERY_KEY } from "../constants/config.constants";

export type LogoutResponse = {
  redirect_url?: string;
};

export const useCheckAuthV2 = (
  config?: Omit<UseQueryOptions<UserData, Error>, "queryKey" | "queryFn">
) => {
  const [initialUser] = useState<UserData | null>(() => UserStorage.getUser());

  return useQuery<UserData, Error>({
    queryKey: USER_QUERY_KEY,
    queryFn: checkAuthV2,
    // validate only if user exists in cache/local storage or cookie exists (in case of google login)
    enabled:
      (!!UserStorage.getUser() || document.cookie.includes("csrftoken")) &&
      config?.enabled,
    refetchOnWindowFocus: false,
    initialData: initialUser || undefined,
    retry: UserStorage.getUser() ? 1 : 0,
    ...config
  });
};

/**
 * Hook for fetching user permissions (new auth flow)
 */
export const useUserPermissionsV2 = (
  user: UserData | null,
  config?: Omit<
    UseQueryOptions<PermissionsAPIResponse, Error>,
    "queryKey" | "queryFn"
  > & { onQuery?: () => void }
) => {
  const isLoginRoute = useFlexibleMatch("/login");
  const { onQuery, ...queryConfig } = config || {};

  return useQuery<PermissionsAPIResponse, Error>({
    queryKey: USER_PERMISSIONS_QUERY_KEY,
    queryFn: () => {
      onQuery?.();
      return permissionService.getPermissions();
    },
    // Refetching causes elements to hide and reappear
    refetchOnWindowFocus: false,
    enabled: !!user || !isLoginRoute,
    retry: user ? 1 : 0,
    staleTime: 30 * 60 * 1000, // 30 minutes
    ...queryConfig
  });
};

/**
 * Hook for email/password login mutation (new auth flow)
 */
export const useLoginMutation = (
  config?: UseMutationOptions<UserData, Error, LoginCredentials>
) => {
  const { onSuccess, ...mutationConfig } = config || {};
  return useMutation<UserData, Error, LoginCredentials>(
    loginWithEmailPassword,
    {
      onSuccess: (userData, variables, context) => {
        if (userData && !userData.mfa?.requires_mfa_verification) {
          UserStorage.setUser(userData);
        }
        queryClient.invalidateQueries(USER_PERMISSIONS_QUERY_KEY);
        onSuccess?.(userData, variables, context);
      },
      ...mutationConfig
    }
  );
};

/**
 * Hook for OTP login mutation (new auth flow)
 */
export const useOTPLoginMutation = (
  config?: UseMutationOptions<UserData, Error, OTPCredentials>
) => {
  const { onSuccess, ...mutationConfig } = config || {};
  return useMutation<UserData, Error, OTPCredentials>(loginWithOTP, {
    onSuccess: (userData, variables, context) => {
      if (userData && !userData.mfa?.requires_mfa_verification) {
        UserStorage.setUser(userData);
      }
      // Update current user data and invalidate permissions
      queryClient.setQueryData(USER_QUERY_KEY, userData);
      queryClient.invalidateQueries(USER_PERMISSIONS_QUERY_KEY);
      onSuccess?.(userData, variables, context);
    },
    ...mutationConfig
  });
};

/**
 * Hook for MFA login mutation (new auth flow)
 */
export const useMFALoginMutation = (
  config?: UseMutationOptions<UserData, Error, string>
) => {
  const { onSuccess, ...mutationConfig } = config || {};
  return useMutation<UserData, Error, string>(submitMFAToken, {
    onSuccess: (userData, variables, context) => {
      if (userData) {
        UserStorage.setUser(userData);
      }
      // Update current user data and invalidate permissions
      queryClient.setQueryData(USER_QUERY_KEY, userData);
      queryClient.invalidateQueries(USER_PERMISSIONS_QUERY_KEY);
      onSuccess?.(userData, variables, context);
    },
    ...mutationConfig
  });
};

/**
 * Hook for logout mutation (new auth flow)
 */
export const useLogoutMutation = (
  config?: UseMutationOptions<LogoutResponse, Error, undefined>
) => {
  const { onSuccess, onError, ...mutationConfig } = config || {};
  return useMutation<LogoutResponse, Error, undefined>(logoutV2, {
    onSuccess: (data, variables, context) => {
      // Clear all queries except config
      queryClient.removeQueries({
        predicate: query => {
          return query.queryKey !== INSTANCE_CONFIG_QUERY_KEY;
        }
      });
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      // Even if logout fails on server, clear local cache
      queryClient.removeQueries({
        predicate: query => {
          return query.queryKey !== INSTANCE_CONFIG_QUERY_KEY;
        }
      });
      onError?.(error, variables, context);
    },
    ...mutationConfig
  });
};

export const invalidateUserQuery = () => {
  return queryClient.invalidateQueries(USER_QUERY_KEY);
};

export const setUserQuery = (user: Partial<UserData>) => {
  return queryClient.setQueryData(USER_QUERY_KEY, user);
};

export const getUserQueryData = () => {
  return queryClient.getQueryData<UserData>(USER_QUERY_KEY);
};

export const removeUserQuery = () => {
  return queryClient.removeQueries(USER_QUERY_KEY);
};

export const removeUserPermissionsQuery = () => {
  return queryClient.removeQueries(USER_PERMISSIONS_QUERY_KEY);
};

export const getUserPermissions = () => {
  const permissionsData = queryClient.getQueryData<PermissionsAPIResponse>(
    USER_PERMISSIONS_QUERY_KEY
  );

  if (!permissionsData) {
    return null;
  }

  return {
    permissions: {
      ...(permissionsData?.objects || {})
    }
  };
};
